import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X, ChevronDown, ChevronUp, Snowflake, Smartphone, Shield, ParkingCircle, Lightbulb, DoorOpen, Cog, Palette } from 'lucide-react';
import { filtersApi, carsApi, analyticsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import { CAR_FEATURES } from '@/lib/carFeatures';

// Icon mapping for car feature categories
const CATEGORY_ICONS = {
  Snowflake, Smartphone, Shield, ParkingCircle, Lightbulb, DoorOpen, Cog, Palette
};

export const SearchFilters = ({ onFilter, compact = false, isHomePage = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { darkMode } = useTheme();
  const { t, language } = useLanguage();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [options, setOptions] = useState({
    brands: [],
    colors: [],
    fuel_types: [],
    transmissions: [],
    doors: [2, 3, 4, 5],
    price_range: { min: 0, max: 1000000 },
    year_range: { min: 2000, max: 2024 },
    horsepower_range: { min: 0, max: 1500 },
    mileage_range: { min: 0, max: 300000 }
  });

  // Initialize filters from URL params
  const getInitialFilters = useCallback(() => {
    return {
      brand: searchParams.get('brand') || '',
      model: searchParams.get('model') || '',
      fuel_type: searchParams.get('fuel_type') || '',
      color: searchParams.get('color') || '',
      transmission: searchParams.get('transmission') || '',
      doors: searchParams.get('doors') || '',
      vehicle_type: searchParams.get('vehicle_type') || '',
      min_price: searchParams.get('min_price') ? Number(searchParams.get('min_price')) : null,
      max_price: searchParams.get('max_price') ? Number(searchParams.get('max_price')) : null,
      min_year: searchParams.get('min_year') ? Number(searchParams.get('min_year')) : null,
      max_year: searchParams.get('max_year') ? Number(searchParams.get('max_year')) : null,
      min_horsepower: searchParams.get('min_horsepower') ? Number(searchParams.get('min_horsepower')) : null,
      max_horsepower: searchParams.get('max_horsepower') ? Number(searchParams.get('max_horsepower')) : null,
      max_mileage: searchParams.get('max_mileage') ? Number(searchParams.get('max_mileage')) : null
    };
  }, [searchParams]);

  // Initialize selected features from URL
  const getInitialFeatures = useCallback(() => {
    const featuresParam = searchParams.get('features');
    return featuresParam ? featuresParam.split(',').filter(f => f) : [];
  }, [searchParams]);

  const [filters, setFilters] = useState(getInitialFilters);
  const [selectedFeatures, setSelectedFeatures] = useState(getInitialFeatures);

  const [priceRange, setPriceRange] = useState([0, 1000000]);
  const [yearRange, setYearRange] = useState([2000, 2024]);
  const [hpRange, setHpRange] = useState([0, 1500]);
  const [mileageRange, setMileageRange] = useState([0, 300000]);

  // Toggle feature selection
  const toggleFeature = (featureKey) => {
    setSelectedFeatures(prev => 
      prev.includes(featureKey) 
        ? prev.filter(f => f !== featureKey)
        : [...prev, featureKey]
    );
  };

  // Toggle category expansion
  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  useEffect(() => {
    loadOptions();
    fetchTotalCount();
  }, []);

  // Update filters from URL params when they change
  useEffect(() => {
    const newFilters = getInitialFilters();
    setFilters(newFilters);
    
    // Update sliders based on URL params
    if (newFilters.min_price !== null || newFilters.max_price !== null) {
      setPriceRange([
        newFilters.min_price || options.price_range.min,
        newFilters.max_price || options.price_range.max
      ]);
    }
    if (newFilters.min_year !== null || newFilters.max_year !== null) {
      setYearRange([
        newFilters.min_year || options.year_range.min,
        newFilters.max_year || options.year_range.max
      ]);
    }
    
    // Fetch count with current filters
    const cleanFilters = Object.fromEntries(
      Object.entries(newFilters).filter(([_, v]) => v !== '' && v !== null)
    );
    fetchTotalCount(cleanFilters);
  }, [searchParams, getInitialFilters, options.price_range.min, options.price_range.max, options.year_range.min, options.year_range.max]);

  const fetchTotalCount = async (filterParams = {}) => {
    try {
      const response = await carsApi.getAll({ ...filterParams, is_active: true });
      if (response && typeof response.total !== 'undefined') {
        setTotalCount(response.total);
      } else if (response && response.data) {
        setTotalCount(response.data.length);
      } else if (Array.isArray(response)) {
        setTotalCount(response.length);
      }
    } catch (err) {
      console.error('Error fetching count:', err);
    }
  };

  const loadOptions = async (currentFilters = {}) => {
    try {
      // Load filter options with counts, passing current filters
      const [optionsResponse, countsResponse] = await Promise.all([
        filtersApi.getOptions(),
        carsApi.getFilterOptionsWithCounts(currentFilters)
      ]);
      
      const data = optionsResponse.data || optionsResponse;
      const counts = countsResponse.data || countsResponse;
      
      if (data && counts) {
        // Map counts to brand/fuel/etc arrays
        const brandsWithCounts = counts.brands || [];
        const fuelTypesWithCounts = counts.fuel_types || [];
        const transmissionsWithCounts = counts.transmissions || [];
        const colorsWithCounts = counts.colors || [];
        const vehicleTypesWithCounts = counts.vehicle_types || [];
        const doorsWithCounts = counts.doors || [];
        
        setOptions(prev => ({
          ...prev,
          brands: brandsWithCounts,
          colors: colorsWithCounts,
          fuel_types: fuelTypesWithCounts,
          transmissions: transmissionsWithCounts,
          vehicle_types: vehicleTypesWithCounts,
          doors: doorsWithCounts,
          price_range: data.price_range || prev.price_range,
          year_range: data.year_range || prev.year_range,
          horsepower_range: data.horsepower_range || prev.horsepower_range,
          mileage_range: data.mileage_range || prev.mileage_range
        }));
        
        // Set initial slider ranges only on first load
        if (!currentFilters.brand && !currentFilters.fuel_type) {
          if (data.price_range && !searchParams.get('min_price')) {
            setPriceRange([data.price_range.min, data.price_range.max]);
          }
          if (data.year_range && !searchParams.get('min_year')) {
            setYearRange([data.year_range.min, data.year_range.max]);
          }
          if (data.horsepower_range) {
            setHpRange([data.horsepower_range.min, data.horsepower_range.max]);
          }
          if (data.mileage_range) {
            setMileageRange([0, data.mileage_range.max]);
          }
        }
        
        // Set total count from the counts endpoint
        if (typeof counts.total !== 'undefined') {
          setTotalCount(counts.total);
        }
      }
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  };

  // Reload filter counts when filters change
  const reloadFilterCounts = async (newFilters) => {
    try {
      const countsResponse = await carsApi.getFilterOptionsWithCounts(newFilters);
      const counts = countsResponse.data || countsResponse;
      
      if (counts) {
        setOptions(prev => ({
          ...prev,
          brands: counts.brands || prev.brands,
          colors: counts.colors || prev.colors,
          fuel_types: counts.fuel_types || prev.fuel_types,
          transmissions: counts.transmissions || prev.transmissions,
          vehicle_types: counts.vehicle_types || prev.vehicle_types,
          doors: counts.doors || prev.doors
        }));
        
        if (typeof counts.total !== 'undefined') {
          setTotalCount(counts.total);
        }
      }
    } catch (err) {
      console.error('Error reloading filter counts:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    const newValue = value === 'all' ? '' : value;
    const newFilters = { ...filters, [key]: newValue };
    setFilters(newFilters);
    
    // Track filter usage for analytics
    if (newValue) {
      analyticsApi.trackFilter(key, newValue).catch(() => {});
    }
    
    // Reload counts with new filter values
    const cleanFilters = Object.fromEntries(
      Object.entries(newFilters).filter(([_, v]) => v !== '' && v !== null)
    );
    reloadFilterCounts(cleanFilters);
  };

  const handlePriceChange = (values) => {
    setPriceRange(values);
    setFilters(prev => ({
      ...prev,
      min_price: values[0] > options.price_range.min ? values[0] : null,
      max_price: values[1] < options.price_range.max ? values[1] : null
    }));
  };

  const handleYearChange = (values) => {
    setYearRange(values);
    setFilters(prev => ({
      ...prev,
      min_year: values[0] > options.year_range.min ? values[0] : null,
      max_year: values[1] < options.year_range.max ? values[1] : null
    }));
  };

  const handleHpChange = (values) => {
    setHpRange(values);
    setFilters(prev => ({
      ...prev,
      min_horsepower: values[0] > options.horsepower_range.min ? values[0] : null,
      max_horsepower: values[1] < options.horsepower_range.max ? values[1] : null
    }));
  };

  const handleMileageChange = (values) => {
    setMileageRange(values);
    setFilters(prev => ({
      ...prev,
      max_mileage: values[1] < (options.mileage_range.max || 300000) ? values[1] : null
    }));
  };

  const handleSearch = () => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== '' && v !== null)
    );
    
    // Build URL params
    const params = new URLSearchParams();
    Object.entries(cleanFilters).forEach(([key, value]) => {
      if (value !== null && value !== '' && value !== undefined) {
        params.set(key, String(value));
      }
    });
    
    // Add selected features
    if (selectedFeatures.length > 0) {
      params.set('features', selectedFeatures.join(','));
      cleanFilters.features = selectedFeatures.join(',');
    }
    
    // Navigate to cars page with filters in URL
    if (isHomePage) {
      navigate(`/cars?${params.toString()}`);
    } else {
      // Update URL and call onFilter
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
      onFilter(cleanFilters);
    }
  };

  const handleReset = () => {
    setFilters({
      brand: '',
      model: '',
      fuel_type: '',
      color: '',
      transmission: '',
      doors: '',
      vehicle_type: '',
      min_price: null,
      max_price: null,
      min_year: null,
      max_year: null,
      min_horsepower: null,
      max_horsepower: null,
      max_mileage: null
    });
    setSelectedFeatures([]);
    setPriceRange([options.price_range.min, options.price_range.max]);
    setYearRange([options.year_range.min, options.year_range.max]);
    setHpRange([options.horsepower_range.min, options.horsepower_range.max]);
    setMileageRange([0, options.mileage_range?.max || 300000]);
    
    if (!isHomePage) {
      window.history.replaceState({}, '', window.location.pathname);
      onFilter({});
    }
  };

  // Get active filters for chips display
  const getActiveFilters = () => {
    const active = [];
    if (filters.brand && filters.brand !== 'all') {
      active.push({ key: 'brand', label: filters.brand, value: filters.brand });
    }
    if (filters.fuel_type && filters.fuel_type !== 'all') {
      active.push({ key: 'fuel_type', label: filters.fuel_type, value: filters.fuel_type });
    }
    if (filters.transmission && filters.transmission !== 'all') {
      active.push({ key: 'transmission', label: filters.transmission, value: filters.transmission });
    }
    if (filters.color && filters.color !== 'all') {
      active.push({ key: 'color', label: filters.color, value: filters.color });
    }
    if (filters.vehicle_type && filters.vehicle_type !== 'all') {
      const labels = { 
        new: t('vehicle_type_new'), 
        used: t('vehicle_type_used'), 
        demo: t('vehicle_type_demo'),
        rental: t('vehicle_type_rental')
      };
      active.push({ key: 'vehicle_type', label: labels[filters.vehicle_type] || filters.vehicle_type, value: filters.vehicle_type });
    }
    if (filters.doors && filters.doors !== 'all') {
      active.push({ key: 'doors', label: `${filters.doors} ${t('doors')}`, value: filters.doors });
    }
    if (filters.min_price !== null && filters.min_price > options.price_range.min) {
      active.push({ key: 'min_price', label: `${language === 'de' ? 'ab' : 'from'} ${formatPrice(filters.min_price)}`, value: filters.min_price });
    }
    if (filters.max_price !== null && filters.max_price < options.price_range.max) {
      active.push({ key: 'max_price', label: `${language === 'de' ? 'bis' : 'to'} ${formatPrice(filters.max_price)}`, value: filters.max_price });
    }
    if (filters.min_year !== null && filters.min_year > options.year_range.min) {
      active.push({ key: 'min_year', label: `${language === 'de' ? 'ab' : 'from'} ${filters.min_year}`, value: filters.min_year });
    }
    if (filters.max_year !== null && filters.max_year < options.year_range.max) {
      active.push({ key: 'max_year', label: `${language === 'de' ? 'bis' : 'to'} ${filters.max_year}`, value: filters.max_year });
    }
    return active;
  };

  const removeFilter = (key) => {
    if (key === 'min_price' || key === 'max_price') {
      setPriceRange([options.price_range.min, options.price_range.max]);
      setFilters(prev => ({ ...prev, min_price: null, max_price: null }));
    } else if (key === 'min_year' || key === 'max_year') {
      setYearRange([options.year_range.min, options.year_range.max]);
      setFilters(prev => ({ ...prev, min_year: null, max_year: null }));
    } else {
      setFilters(prev => ({ ...prev, [key]: '' }));
    }
  };

  const activeFilters = getActiveFilters();

  const formatPrice = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div 
      className="transition-colors border rounded-lg overflow-hidden" 
      style={{ 
        backgroundColor: `hsl(var(--card))`,
        borderColor: `hsl(var(--border))`
      }}
      data-testid="search-filters"
    >
      {/* Compact Filter Bar */}
      <div className="p-4">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
          {/* Search Input */}
          <div className="flex flex-col gap-1 flex-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('search_placeholder')}</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                type="text"
                placeholder={t('search_placeholder')}
                value={filters.model}
                onChange={(e) => handleFilterChange('model', e.target.value)}
                className="w-full h-10 pl-10 pr-4 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                data-testid="filter-search"
              />
            </div>
          </div>

          {/* Main Dropdown Filters */}
          <div className="flex flex-wrap lg:flex-nowrap gap-4 flex-1">
            {/* Brand */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('filter_brand')}</label>
              <Select
                value={filters.brand || 'all'}
                onValueChange={(value) => handleFilterChange('brand', value)}
              >
                <SelectTrigger className="h-10 min-w-[140px] rounded-md border-border text-sm" data-testid="filter-brand">
                  <SelectValue placeholder={t('filter_all_brands')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('filter_all_brands')}</SelectItem>
                  {options.brands && options.brands.map(item => (
                    <SelectItem key={item.value || item} value={item.value || item}>
                      {item.value || item} {item.count !== undefined && <span className="text-muted-foreground">({item.count})</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Fuel Type */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('filter_fuel')}</label>
              <Select
                value={filters.fuel_type || 'all'}
                onValueChange={(value) => handleFilterChange('fuel_type', value)}
              >
                <SelectTrigger className="h-10 min-w-[120px] rounded-md border-border text-sm" data-testid="filter-fuel">
                  <SelectValue placeholder={t('filter_all_types')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('filter_all_types')}</SelectItem>
                  {options.fuel_types && options.fuel_types.map(item => (
                    <SelectItem key={item.value || item} value={item.value || item}>
                      {item.value || item} {item.count !== undefined && <span className="text-muted-foreground">({item.count})</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Transmission */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('filter_transmission')}</label>
              <Select
                value={filters.transmission || 'all'}
                onValueChange={(value) => handleFilterChange('transmission', value)}
              >
                <SelectTrigger className="h-10 min-w-[130px] rounded-md border-border text-sm" data-testid="filter-transmission">
                  <SelectValue placeholder={t('filter_all_types')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('filter_all_types')}</SelectItem>
                  {options.transmissions && options.transmissions.map(item => (
                    <SelectItem key={item.value || item} value={item.value || item}>
                      {item.value || item} {item.count !== undefined && <span className="text-muted-foreground">({item.count})</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Spacer to push buttons to the right */}
            <div className="flex-1" />

            {/* Advanced Filters Toggle - aligned to bottom */}
            <div className="flex flex-col justify-end">
              <Button
                variant="outline"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className={`h-10 w-12 rounded-md border-border flex items-center justify-center ${showAdvanced ? 'bg-primary text-primary-foreground' : ''}`}
                data-testid="toggle-advanced-filters"
              >
                <SlidersHorizontal size={18} />
              </Button>
            </div>

            {/* Search Button - aligned to bottom */}
            <div className="flex flex-col justify-end">
              <button
                onClick={handleSearch}
                className="h-10 px-7 rounded-md flex items-center justify-center transition-colors"
                style={{ 
                  backgroundColor: 'var(--color-primary)', 
                  color: 'var(--color-secondary)' 
                }}
                data-testid="filter-search-btn"
              >
                <Search size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Active Filter Chips + Result Count */}
        {(activeFilters.length > 0 || totalCount > 0) && (
          <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="active-filters">
            {/* Result Count */}
            <span className="text-sm font-medium text-muted-foreground">
              {totalCount} {language === 'de' ? 'Fahrzeuge gefunden' : 'vehicles found'}
            </span>
            
            {activeFilters.length > 0 && (
              <>
                <span className="text-muted-foreground">|</span>
                {activeFilters.map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => removeFilter(filter.key)}
                    className="inline-flex items-center gap-1.5 px-3 py-1 text-xs bg-primary/10 text-primary border border-primary/20 rounded-full hover:bg-primary/20 transition-colors"
                    data-testid={`filter-chip-${filter.key}`}
                  >
                    <span>{filter.label}</span>
                    <X size={12} />
                  </button>
                ))}
                <button
                  onClick={handleReset}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors underline"
                  data-testid="clear-all-filters"
                >
                  {t('filter_clear_all')}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Advanced Filters Panel */}
      {showAdvanced && (
        <div className="border-t border-border p-4 bg-muted/30" data-testid="advanced-filters-panel">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Color */}
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-2">{t('filter_color')}</label>
              <Select
                value={filters.color || 'all'}
                onValueChange={(value) => handleFilterChange('color', value)}
              >
                <SelectTrigger className="h-10 w-full rounded-md border-border text-sm" data-testid="filter-color">
                  <SelectValue placeholder={t('filter_all_colors')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('filter_all_colors')}</SelectItem>
                  {options.colors.map(item => (
                    <SelectItem key={item.value || item} value={item.value || item}>
                      {item.value || item} {item.count !== undefined && <span className="text-muted-foreground">({item.count})</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Vehicle Type */}
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-2">{t('filter_vehicle_type')}</label>
              <Select
                value={filters.vehicle_type || 'all'}
                onValueChange={(value) => handleFilterChange('vehicle_type', value)}
              >
                <SelectTrigger className="h-10 w-full rounded-md border-border text-sm" data-testid="filter-vehicle-type">
                  <SelectValue placeholder={t('filter_all_types')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('filter_all_types')}</SelectItem>
                  {options.vehicle_types && options.vehicle_types.length > 0 ? (
                    options.vehicle_types.map(item => {
                      const labels = { 
                        new: t('vehicle_type_new'), 
                        used: t('vehicle_type_used'), 
                        demo: t('vehicle_type_demo'),
                        rental: t('vehicle_type_rental')
                      };
                      const value = item.value || item;
                      return (
                        <SelectItem key={value} value={value}>
                          {labels[value] || value} {item.count !== undefined && <span className="text-muted-foreground">({item.count})</span>}
                        </SelectItem>
                      );
                    })
                  ) : (
                    <>
                      <SelectItem value="new">{t('vehicle_type_new')}</SelectItem>
                      <SelectItem value="used">{t('vehicle_type_used')}</SelectItem>
                      <SelectItem value="demo">{t('vehicle_type_demo')}</SelectItem>
                      <SelectItem value="rental">{t('vehicle_type_rental')}</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Doors */}
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-2">{t('filter_doors')}</label>
              <Select
                value={filters.doors || 'all'}
                onValueChange={(value) => handleFilterChange('doors', value)}
              >
                <SelectTrigger className="h-10 w-full rounded-md border-border text-sm" data-testid="filter-doors">
                  <SelectValue placeholder={t('filter_all_types')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('filter_all_types')}</SelectItem>
                  {options.doors && options.doors.map(item => {
                    const doorValue = item.value || item;
                    const doorCount = item.count;
                    return (
                      <SelectItem key={doorValue} value={String(doorValue)}>
                        {doorValue} {t('doors')} {doorCount !== undefined && <span className="text-muted-foreground">({doorCount})</span>}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Range Sliders */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Price Range */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('filter_price')}</label>
                <span className="text-xs text-muted-foreground">
                  {formatPrice(priceRange[0])} - {formatPrice(priceRange[1])}
                </span>
              </div>
              <Slider
                value={priceRange}
                min={options.price_range.min}
                max={options.price_range.max}
                step={5000}
                onValueChange={handlePriceChange}
                className="w-full"
                data-testid="filter-price-slider"
              />
            </div>

            {/* Year Range */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('filter_year')}</label>
                <span className="text-xs text-muted-foreground">
                  {yearRange[0]} - {yearRange[1]}
                </span>
              </div>
              <Slider
                value={yearRange}
                min={options.year_range.min}
                max={options.year_range.max}
                step={1}
                onValueChange={handleYearChange}
                className="w-full"
                data-testid="filter-year-slider"
              />
            </div>

            {/* Horsepower Range */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('filter_horsepower')}</label>
                <span className="text-xs text-muted-foreground">
                  {hpRange[0]} - {hpRange[1]} {t('hp')}
                </span>
              </div>
              <Slider
                value={hpRange}
                min={options.horsepower_range.min}
                max={options.horsepower_range.max}
                step={10}
                onValueChange={handleHpChange}
                className="w-full"
                data-testid="filter-hp-slider"
              />
            </div>

            {/* Mileage Range */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('filter_mileage')}</label>
                <span className="text-xs text-muted-foreground">
                  {language === 'de' ? 'bis' : 'up to'} {mileageRange[1].toLocaleString('de-DE')} km
                </span>
              </div>
              <Slider
                value={mileageRange}
                min={0}
                max={options.mileage_range?.max || 300000}
                step={5000}
                onValueChange={handleMileageChange}
                className="w-full"
                data-testid="filter-mileage-slider"
              />
            </div>
          </div>

          {/* Features Checkboxes */}
          <div className="mt-8 pt-6 border-t border-border">
            <h3 className="text-sm font-semibold mb-4">{language === 'de' ? 'Ausstattung' : 'Features'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(CAR_FEATURES).map(([categoryKey, category]) => {
                const IconComponent = CATEGORY_ICONS[category.icon];
                return (
                <div key={categoryKey} className="bg-background/50 rounded-lg p-3">
                  <button
                    onClick={() => toggleCategory(categoryKey)}
                    className="flex items-center justify-between w-full text-left mb-2"
                  >
                    <span className="text-sm font-medium flex items-center gap-2">
                      {IconComponent && <IconComponent size={16} className="text-primary" />}
                      <span>{language === 'de' ? category.name_de : category.name_en}</span>
                    </span>
                    {expandedCategories[categoryKey] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  
                  {expandedCategories[categoryKey] && (
                    <div className="space-y-2 mt-2">
                      {category.features.map(feature => (
                        <label 
                          key={feature.key}
                          className="flex items-center gap-2 text-sm cursor-pointer hover:text-primary transition-colors"
                        >
                          <Checkbox
                            checked={selectedFeatures.includes(feature.key)}
                            onCheckedChange={() => toggleFeature(feature.key)}
                            className="h-4 w-4"
                          />
                          <span>{language === 'de' ? feature.de : feature.en}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )})}
            </div>
            
            {/* Selected Features Summary */}
            {selectedFeatures.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-xs text-muted-foreground">{language === 'de' ? 'Ausgewählt:' : 'Selected:'}</span>
                {selectedFeatures.map(featureKey => {
                  // Find feature label
                  let label = featureKey;
                  for (const cat of Object.values(CAR_FEATURES)) {
                    const f = cat.features.find(feat => feat.key === featureKey);
                    if (f) {
                      label = language === 'de' ? f.de : f.en;
                      break;
                    }
                  }
                  return (
                    <button
                      key={featureKey}
                      onClick={() => toggleFeature(featureKey)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full hover:bg-primary/20"
                    >
                      {label}
                      <X size={10} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Reset Button */}
          <div className="flex justify-end mt-6">
            <Button
              variant="ghost"
              onClick={handleReset}
              className="text-sm"
              data-testid="filter-reset"
            >
              <X size={16} className="mr-2" />
              {t('filter_reset')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFilters;
